import clsx from 'clsx';
import React from 'react';

import { Loader } from 'src/components/loading/Loader';

import { SettingsColumnProvider } from './SettingsColumnContext';
import { SettingsPageHeader } from './SettingsPageHeader';
import { SettingsPageLoadingContext } from './SettingsPageLoading';

interface SettingsPageColumnProps {
  title: string;
  description?: string;
  docsUrl?: string;
  /** Beside the title; see SettingsPageHeader. */
  titleBadge?: React.ReactNode;
  /** For pages that draw their own header, such as the custom field editor. */
  hideHeader?: boolean;
  children?: React.ReactNode;
}

/**
 * The single centered column every settings page sits in. The title comes in
 * as a prop so the shell can read it from the registry; pages only pass their
 * groups and cards.
 */
export const SettingsPageColumn = ({
  title,
  description,
  docsUrl,
  titleBadge,
  hideHeader,
  children,
}: SettingsPageColumnProps) => {
  const [pendingLoads, setPendingLoads] = React.useState(0);
  const changePendingLoads = React.useCallback(
    (delta: number) => setPendingLoads((count) => count + delta),
    [],
  );
  const isLoading = pendingLoads > 0;

  return (
    <SettingsPageLoadingContext.Provider value={changePendingLoads}>
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      )}
      {/* Hidden rather than unmounted, so the page keeps its requests going. */}
      <div
        className={clsx(
          'mx-auto box-border w-full max-w-[760px] px-5 pt-16 pb-20 lg:px-10',
          isLoading && 'hidden',
        )}
      >
        {!hideHeader && (
          <SettingsPageHeader
            title={title}
            description={description}
            docsUrl={docsUrl}
            badge={titleBadge}
          />
        )}
        {children && (
          <div className={clsx('flex flex-col gap-10', !hideHeader && 'mt-10')}>
            <SettingsColumnProvider>{children}</SettingsColumnProvider>
          </div>
        )}
      </div>
    </SettingsPageLoadingContext.Provider>
  );
};
